import { useMemo } from "react";
import { useSelector } from "react-redux";
import { BotType } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { TIME_RANGE_1_MONTHS } from "@shared/common/constants";
import { useIncludeArchived } from "features/filters/hooks";

/** A bot's accumulated profit over the last month, one value per day. */
export type NewLookBotSparklines = Record<number, number[]>;

/**
 * New look: each bot's accumulated profit over the last month, for the bot
 * cards' sparklines. Reads the same bots-graphs query (same arguments) the
 * performance panel above the list and the dashboard bot cards already load,
 * so it adds no request. `enabled` is false where that panel isn't shown
 * (TV-to-signals bots, channel configurations): there the cards have no
 * sparkline at all rather than a request of their own.
 * Returns undefined while the graphs load.
 */
const useNewLookBotSparklines = (
  botType: BotType,
  enabled: boolean
): NewLookBotSparklines | undefined => {
  const includeArchived = useIncludeArchived(botType);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const args = useMemo(
    () => ({
      bot_type: botType,
      time_range: TIME_RANGE_1_MONTHS,
      include_archived: includeArchived,
    }),
    [botType, includeArchived]
  );
  const { data } = sharedLib.sharedApi.bots.useGetBotsGraphsDataQuery(args, {
    skip: !enabled,
  });

  return useMemo(() => {
    const graphs = data?.data;
    const count = graphs?.timestamps?.length;
    if (!graphs || !count) return undefined;
    const byBot = graphs.accumulated_profit?.[displayCurrency] || {};
    const result: NewLookBotSparklines = {};
    Object.entries(byBot).forEach(([botId, points]) => {
      // Sparse: a point per changed day; carry the last value forward (as
      // the aggregated chart does).
      let previous = 0;
      const values: number[] = [];
      for (let index = 0; index < count; index++) {
        const point = points?.[index];
        if (point !== undefined && point !== null) previous = point;
        values.push(previous);
      }
      result[Number(botId)] = values;
    });
    return result;
  }, [data, displayCurrency]);
};

export default useNewLookBotSparklines;

/** One bot's sparkline (undefined while the graphs load or without data). */
export const getNewLookSparkline = (
  sparklines: NewLookBotSparklines | undefined,
  botId: number
) => sparklines?.[botId];
